'use client';

import { useAuth } from '@/context/AuthContext';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { api } from '@/lib/trpc/react';
import { formatDateUK } from '@/utils/utils';
import { PopoverClose } from '@radix-ui/react-popover';
import { formatTimeAgo } from '@unriddle/api/slack';
import { ThumbsUp } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from './Button/Button';
import SidebarItem from './Sidebars/SidebarItem';
import { Form, FormField, usePersistedForm } from './ui/Form';
import { Popover, PopoverContent, PopoverTrigger } from './ui/Popover';

const feedbackSchema = z.object({
  feedback: z.string().min(3),
});

const Feedback: React.FC = () => {
  const { user } = useAuth();
  const { mutate: appendSpreadsheet } =
    api.googleSpreadsheets.append.useMutation();
  const [open, setOpen] = React.useState(false);
  const { track } = useAnalytics();

  const form = usePersistedForm({
    schema: feedbackSchema,
    storageKey: 'feedback-form',
    ...(typeof window !== 'undefined' && { storage: localStorage }),
  });

  const onSubmit = async ({ feedback }: { feedback: string }) => {
    form.setValue('feedback', '');
    const date = new Date();

    const newRow = {
      Timestamp: formatDateUK(date),
      Feedback: feedback,
    };
    const currentUrl = window.location.href;
    const answers = user?.onboardingResponse?.[0];
    const messageParts = [
      'New feedback submission:',
      `- From: ${user ? user.email : 'visitor'}`,
      `- URL: ${currentUrl}`,
      answers?.hearAboutUs ? `- Source: ${answers?.hearAboutUs}` : null,
      answers?.purpose ? `- Purpose: ${answers?.purpose}` : null,
      answers?.educationLevel
        ? `- Education Level: ${answers?.educationLevel}`
        : null,
      user?.createdAt
        ? `- Account created: ${formatTimeAgo(new Date(user?.createdAt))}`
        : null,
      feedback ? `- Feedback: ${feedback}` : null,
    ].filter(Boolean);

    const slackMessage = messageParts.join('\n');

    track('feedback_submit', {
      feedback,
      url: currentUrl,
    });

    appendSpreadsheet({ feedback: newRow, slackMessage });

    toast.success('Feedback submitted. Thanks!');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SidebarItem icon={ThumbsUp}>Feedback</SidebarItem>
      </PopoverTrigger>
      <PopoverContent align="start">
        <Form {...form}>
          <form id="feedback-form" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              name="feedback"
              control={form.control}
              render={({ field }) => (
                <textarea
                  {...field}
                  rows={4}
                  placeholder="How can we improve Anara?"
                  className="w-full p-2 text-sm mb-2 h-full focus:outline-none placeholder:text-muted-foreground bg-popover resize-none"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      form.handleSubmit(onSubmit)();
                    }
                  }}
                />
              )}
            />

            <div className="flex flex-row justify-between p-1">
              <PopoverClose asChild>
                <Button size="xs" variant="secondary" type="button">
                  Cancel
                </Button>
              </PopoverClose>
              <Button type="submit" size="xs">
                Submit
              </Button>
            </div>
          </form>
        </Form>
      </PopoverContent>
    </Popover>
  );
};

export default Feedback;
